import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

export default function Kontakt() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    description: '',
    website_url: '' // Honeypot felt til spam-beskyttelse
  });

  const [status, setStatus] = useState({
    submitting: false,
    success: false,
    error: null
  });

  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Navn skal udfyldes';
    if (!formData.phone.trim()) {
      newErrors.phone = 'Telefonnummer skal udfyldes';
    } else if (!/^[0-9\s+]{8,15}$/.test(formData.phone.trim())) {
      newErrors.phone = 'Indtast et gyldigt telefonnummer (mindst 8 tal)';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'E-mail skal udfyldes';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Indtast en gyldig e-mailadresse';
    }
    if (!formData.description.trim()) newErrors.description = 'Beskrivelse skal udfyldes';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Fjern fejlbesked når brugeren skriver
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setStatus({ submitting: true, success: false, error: null });

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setStatus({ submitting: false, success: true, error: null });
        setFormData({
          name: '',
          phone: '',
          email: '',
          description: '',
          website_url: ''
        });
      } else {
        throw new Error('Der skete en fejl ved afsendelsen. Prøv venligst igen.');
      }
    } catch (err) {
      setStatus({
        submitting: false,
        success: false,
        error: err.message || 'Der kunne ikke oprettes forbindelse til serveren.'
      });
    }
  };

  return (
    <>
      <SEO 
        title="Kontakt" 
        description="Kontakt Tømrermester Mikkel Juler ApS i Egtved for et gratis og uforpligtende tilbud på tømrerarbejde. Ring til os på 28 45 50 12." 
      />

      {/* Hero / Header */}
      <section className="bg-brand-wood-light py-12 md:py-16 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <nav className="flex mb-4 text-xs sm:text-sm text-gray-500 gap-2 items-center">
              <Link to="/" className="hover:text-brand-wood">Forside</Link>
              <i className="fa-solid fa-chevron-right text-[10px]" />
              <span className="text-brand-wood-dark font-semibold">Kontakt</span>
            </nav>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-brand-dark tracking-tight leading-tight">
              Kontakt os i dag
            </h1>
            <p className="mt-4 text-base sm:text-lg text-brand-dark-light leading-relaxed">
              Går du med byggeplaner, eller har du brug for professionel rådgivning? Vi står altid klar til at tage en uforpligtende snak om dit projekt.
            </p>
          </div>
        </div>
      </section>

      {/* Kontaktformular og Info */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Kontaktformular */}
            <div className="lg:col-span-7 bg-brand-cream p-6 sm:p-10 rounded-2xl border border-gray-100 shadow-xs">
              <h2 className="text-2xl font-bold text-brand-dark mb-2">Send os en besked</h2>
              <p className="text-brand-dark-light text-sm sm:text-base mb-8">
                Udfyld formularen herunder, så vender vi tilbage til dig så hurtigt som muligt – typisk inden for 24 timer.
              </p>

              {status.success ? (
                <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 p-6 rounded-xl space-y-3">
                  <div className="flex items-center gap-3">
                    <i className="fa-solid fa-circle-check text-2xl text-emerald-600" />
                    <h3 className="font-bold text-lg">Tak for din henvendelse!</h3>
                  </div>
                  <p className="text-sm">
                    Vi har modtaget din besked og vil læse den igennem med det samme. Tømrermester Mikkel Juler vender tilbage til dig hurtigst muligt.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Honeypot felt til spam-beskyttelse - ABSOLUT SKJULT for mennesker */}
                  <div className="sr-only absolute -top-9999 -left-9999" aria-hidden="true">
                    <label htmlFor="website_url">Efterlad dette felt tomt</label>
                    <input
                      type="text"
                      id="website_url"
                      name="website_url"
                      value={formData.website_url}
                      onChange={handleChange}
                      tabIndex="-1"
                      autoComplete="off"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {/* Navn */}
                    <div className="space-y-2">
                      <label htmlFor="name" className="block text-sm font-semibold text-brand-dark">
                        Navn <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 rounded-xl border bg-white focus:outline-none focus:ring-2 focus:ring-brand-green/20 focus:border-brand-green transition-colors ${
                          errors.name ? 'border-red-500' : 'border-gray-200'
                        }`}
                        placeholder="Dit fulde navn"
                      />
                      {errors.name && (
                        <p className="text-red-500 text-xs font-medium">{errors.name}</p>
                      )}
                    </div>

                    {/* Telefonnummer */}
                    <div className="space-y-2">
                      <label htmlFor="phone" className="block text-sm font-semibold text-brand-dark">
                        Telefonnummer <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 rounded-xl border bg-white focus:outline-none focus:ring-2 focus:ring-brand-green/20 focus:border-brand-green transition-colors ${
                          errors.phone ? 'border-red-500' : 'border-gray-200'
                        }`}
                        placeholder="F.eks. 28 45 50 12"
                      />
                      {errors.phone && (
                        <p className="text-red-500 text-xs font-medium">{errors.phone}</p>
                      )}
                    </div>
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <label htmlFor="email" className="block text-sm font-semibold text-brand-dark">
                      E-mailadresse <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 rounded-xl border bg-white focus:outline-none focus:ring-2 focus:ring-brand-green/20 focus:border-brand-green transition-colors ${
                        errors.email ? 'border-red-500' : 'border-gray-200'
                      }`}
                      placeholder="F.eks. navn@mail.dk"
                    />
                    {errors.email && (
                      <p className="text-red-500 text-xs font-medium">{errors.email}</p>
                    )}
                  </div>

                  {/* Beskrivelse */}
                  <div className="space-y-2">
                    <label htmlFor="description" className="block text-sm font-semibold text-brand-dark">
                      Beskriv dit projekt <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      rows="5"
                      value={formData.description}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 rounded-xl border bg-white focus:outline-none focus:ring-2 focus:ring-brand-green/20 focus:border-brand-green transition-colors resize-none ${
                        errors.description ? 'border-red-500' : 'border-gray-200'
                      }`}
                      placeholder="Fortæl os kort om, hvad du skal have lavet, f.eks. størrelse på terrasse, tagtype eller antal vinduer..."
                    />
                    {errors.description && (
                      <p className="text-red-500 text-xs font-medium">{errors.description}</p>
                    )}
                  </div>

                  {status.error && (
                    <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-xl text-sm">
                      <i className="fa-solid fa-circle-exclamation mr-2" />
                      {status.error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status.submitting}
                    className="w-full bg-brand-green hover:bg-brand-green/90 disabled:bg-gray-400 text-white font-semibold py-4 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 cursor-pointer flex items-center justify-center gap-2"
                  >
                    {status.submitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Sender besked...</span>
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-paper-plane" />
                        <span>Send uforpligtende henvendelse</span>
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>

            {/* Kontaktoplysninger & Kort */}
            <div className="lg:col-span-5 space-y-8">
              {/* Info boks */}
              <div className="bg-brand-dark text-white p-8 rounded-2xl border border-gray-800 space-y-6">
                <h3 className="text-xl font-bold text-brand-wood">Firmaoplysninger</h3>
                
                <div className="space-y-4 text-sm sm:text-base">
                  <div className="flex gap-4 items-start">
                    <div className="w-10 h-10 rounded-xl bg-brand-dark-light text-brand-wood flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-building" />
                    </div>
                    <div>
                      <span className="block text-xs text-gray-400 font-semibold uppercase tracking-wider">Virksomhed</span>
                      <span className="font-bold">Tømrermester Mikkel Juler ApS</span>
                      <span className="block text-xs text-gray-500 mt-0.5">CVR-nr: 42891045</span>
                    </div>
                  </div>

                  <div className="flex gap-4 items-start">
                    <div className="w-10 h-10 rounded-xl bg-brand-dark-light text-brand-wood flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-phone" />
                    </div>
                    <div>
                      <span className="block text-xs text-gray-400 font-semibold uppercase tracking-wider">Telefon</span>
                      <a href="tel:+4528455012" className="font-bold hover:text-brand-wood transition-colors whitespace-nowrap">
                        28 45 50 12
                      </a>
                    </div>
                  </div>

                  <div className="flex gap-4 items-start">
                    <div className="w-10 h-10 rounded-xl bg-brand-dark-light text-brand-wood flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-envelope" />
                    </div>
                    <div>
                      <span className="block text-xs text-gray-400 font-semibold uppercase tracking-wider">E-mail</span>
                      <a href="mailto:Mikkeljuler@gmail.com" className="font-bold hover:text-brand-wood transition-colors whitespace-nowrap">
                        Mikkeljuler@gmail.com
                      </a>
                    </div>
                  </div>

                  <div className="flex gap-4 items-start">
                    <div className="w-10 h-10 rounded-xl bg-brand-dark-light text-brand-wood flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-location-dot" />
                    </div>
                    <div>
                      <span className="block text-xs text-gray-400 font-semibold uppercase tracking-wider">Adresse</span>
                      <a 
                        href="https://maps.google.com/?q=Koldingvej+49B,+6040+Egtved" 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="font-bold hover:text-brand-wood transition-colors"
                      >
                        Koldingvej 49B, 6040 Egtved
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Google Maps Integration */}
              <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-xs h-72 relative">
                <iframe 
                  title="Tømrermester Mikkel Juler ApS adresse på Google Maps"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2254.896792404595!2d9.30395357731766!3d55.62119190132514!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x464c7980860368df%3A0x82068e82d92634e5!2sKoldingvej%2049B%2C%206040%20Egtved!5e0!3m2!1sda!2sdk!4v1719820000000!5m2!1sda!2sdk" 
                  className="w-full h-full border-0"
                  allowFullScreen="" 
                  loading="lazy" 
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
